import React, { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client';
import Header from '../components/Header';

import Load from '../components/Load';
import Createpost from '../components/Createpost';
import PostRouter from '../components/PostRouter';

import Events from '../components/Events';
import { LISTAR_POSTS, POSTS_POR_LIGA, Post, PostsPorLigaData, PostsPorLigaVars, ListarPostsResponse, ListarPostsVars } from '../services/postService';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';

const Home: React.FC = () => {
    
    const user = useAuth()?.user || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);
    const atletaId = user?.atleta?.id ?? '';
    const isAdmin = user?.role === 'ADMIN';
    const { currentProfile } = useProfile();
    const isLigaProfile = currentProfile?.tipo === 'LIGA';
    const ligaId = currentProfile?.referenciaId;

    // Variável page é usada apenas para controle do fetchMore (incremento manual)
    // O Apollo Client gerencia a paginação internamente através do fetchMore
    const [page, setPage] = useState(0);
    
    // Query para posts gerais (quando perfil é Atleta ou não há perfil)
    const { data: postsData, loading: loadingPosts, error: errorPosts, fetchMore: fetchMorePosts, refetch: refetchPosts } = useQuery<ListarPostsResponse, ListarPostsVars>(LISTAR_POSTS, {
        variables: { page: 0, size: 10, curtidorAtletaId: atletaId, curtidorTorneioId: '' },
        skip: isLigaProfile,
    });

    // Query para posts por Liga (quando perfil é Liga)
    // Garantimos que ligaId nunca seja string vazia através do skip
    const { data: ligaData, loading: loadingLiga, error: errorLiga, fetchMore: fetchMoreLiga, refetch: refetchLiga } = useQuery<PostsPorLigaData, PostsPorLigaVars>(POSTS_POR_LIGA, {
        variables: { ligaId: ligaId || '', page: 0, size: 10, curtidorAtletaId: undefined, curtidorTorneioId: undefined },
        skip: !isLigaProfile || !ligaId,
    });

    const loading = isLigaProfile ? loadingLiga : loadingPosts;
    const error = isLigaProfile ? errorLiga : errorPosts;

    const posts = isLigaProfile 
        ? (ligaData?.postsPorLiga?.items ?? [])
        : (postsData?.posts ?? []);

    // Verifica se há mais páginas disponíveis para paginação
    // Para POSTS_POR_LIGA, usamos totalPages da resposta
    // Para LISTAR_POSTS, não temos totalPages, então assumimos que sempre pode haver mais
    const hasMore = isLigaProfile 
        ? ligaData?.postsPorLiga 
            ? (ligaData.postsPorLiga.page ?? 0) < (ligaData.postsPorLiga.totalPages ?? 1) - 1
            : false
        : true; // LISTAR_POSTS não retorna totalPages, manter comportamento atual

    const handlePostCreated = async () => {
        // Recarrega apenas a primeira página com size padrão
        if (isLigaProfile) {
            await refetchLiga({ page: 0, size: 10 });
        } else {
            await refetchPosts({ page: 0, size: 10 });
        }
        setPage(0);
    };

    const handleLoadMore = () => {
        if (isLigaProfile) {
            if (!ligaId) return; // Garantir que ligaId existe antes de fazer fetch
            fetchMoreLiga({
                variables: { ligaId, page: page + 1, size: 10, curtidorAtletaId: undefined, curtidorTorneioId: undefined },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    return { 
                        postsPorLiga: { 
                            ...prev.postsPorLiga, 
                            items: [...prev.postsPorLiga.items, ...fetchMoreResult.postsPorLiga.items] 
                        } 
                    };
                },
            });
        } else {
            fetchMorePosts({
                variables: { page: page + 1, size: 10, curtidorAtletaId: atletaId, curtidorTorneioId: '' },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    return { posts: [...prev.posts, ...fetchMoreResult.posts] };
                },
            });
        }
        setPage((p) => p + 1);
    };

    return (
        <Fragment>
            <Header />
            {/* <Leftnav /> */}

            <div className="main-content right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left">
                        <div className="row feed-body">
                            <div className="col-xl-8 col-xxl-9 col-lg-8">
                                {/* <Storyslider /> */}
                                {!isAdmin && (
                                    <Createpost
                                        autorAtleta={user?.atleta}
                                        ligaId={isLigaProfile ? ligaId : undefined}
                                        perfilAtivo={currentProfile}
                                        onPostCreated={handlePostCreated}
                                    />
                                )}
                                {loading && <Load />}
                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        Erro ao carregar posts.
                                    </div>
                                )}
                                {posts.map((post) => (
                                    <PostRouter
                                        key={post.id}
                                        post={post}
                                        torneioId={post.autorTorneio?.id}
                                        ligaId={post.autorLiga?.id}
                                        perfilAtivo={currentProfile}
                                        readOnly={isLigaProfile}
                                    />
                                ))}
                                {posts.length > 0 && hasMore && (
                                    <div className="text-center">
                                        <button className="btn btn-primary" onClick={handleLoadMore}>
                                            Carregar mais
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="col-xl-4 col-xxl-3 col-lg-4 ps-lg-0">
                                <Events />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </Fragment>
    );
};

export default Home;
